import * as React from "react";
import { useState, useEffect, useRef } from "react";
import Header from "./Header";
import { makeStyles, Button as FluentButton,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import Button from "./Button";
import ReportsTree from './ReportsTree';
import ReportParams from './ReportParams';
import TemplateTab from './TemplateTab';
import ErrorBoundary from './ErrorBoundary';
import { TabList, Tab } from "@fluentui/react-tabs";
import { ArrowClockwise20Regular as RefreshIcon } from "@fluentui/react-icons";
import AiAgent from "./AiAgent";
import getApiConfig  from "../../api/apiConfig";
import api from "../../api/api";
import { getErrorMessage, responseError } from "../../api/apiError";
import { ErrorMessages } from "../../constants/messages";
import { fetchLoggedInUser, type LoggedInUser } from "./LoggedInUserApiService";

interface AppProps {
  title: string;
  version: string;
}

const TEMPLATE_TASK_PANE_WIDTH = 548;

function setTaskPaneWidth(width: number): void {
  try {
    if (Office.context.requirements && Office.context.requirements.isSetSupported("TaskPaneApi", "1.1")) {
      const officeAny = Office as any;
      if (
        officeAny.extensionLifeCycle &&
        officeAny.extensionLifeCycle.taskpane &&
        typeof officeAny.extensionLifeCycle.taskpane.setWidth === "function"
      ) {
        officeAny.extensionLifeCycle.taskpane.setWidth(width);
      }
    }
  } catch (error) {
    // Keep the add-in usable with the default task pane width when the API is unavailable.
  }
}

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    fontFamily: "Arial regular",
    fontSize: "12px",
    color: "#6D6E71",
  },
  content: {
    flexGrow: 1,
    overflowY: "auto",
    padding: "10px 20px",
  },
  versionFooter: {
    flexShrink: 0,
    borderTop: "1px solid #ccc",
    padding: "10px 20px",
    color: "gray",
  },
  versionText: {
    margin: 0,
  },
});

const App: React.FC<AppProps> = (props: AppProps) => {
  const styles = useStyles();

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isBmxReport, setIsBmxReport] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authToken, setAuthToken] = useState<string>("");
  const [identityProvider, setIdentityProvider] = useState<string>("Entra");
  const [oktaIdToken, setOktaIdToken] = useState<string>("");
  const [userID, setUserID] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [forceAnalyzers, setForceAnalyzers] = useState<boolean>(false);
  const [shouldRefresh, setShouldRefresh] = useState<Date>(new Date());
  const [aiAgentActive, setAiAgentActive] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- only read by the AI Assist tab, currently commented out
  const [aiAgentDisabled, setAiAgentDisabled] = useState<boolean>(false);
  const [templateTabActive, setTemplateTabActive] = useState<boolean>(false);
  // Null until BuilderMetrix answers, so a failed or in-flight read leaves the gated tabs closed
  // rather than briefly offering them.
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>(isBmxReport ? "data" : "analyzers");
  const [error, setError] = useState<string>("");
  const [showApiBaseUrlDialog, setShowApiBaseUrlDialog] = useState<boolean>(false);
  const [showConnectionSelector, setShowConnectionSelector] = useState<boolean>(false);
  const [connectionNames, setConnectionNames] = useState<string[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>("");
  const [connectionNameSelected, setConnectionNameSelected] = useState<boolean>(false);
  const templateSaveNavigationTimeoutRef = useRef<number | null>(null);
  const preTemplateTaskPaneWidthRef = useRef<number | null>(null);

  const apiBaseUrl = getApiConfig().baseUrl;

  function userChanged(token: string, oktaIdToken: string, ID: string, name: string, identityProvider: string = "Entra") {
    setAuthToken(token);
    setOktaIdToken(oktaIdToken);
    setUserID(ID);
    setUserName(name);
    setIdentityProvider(identityProvider);
    setIsLoading(false);
    setAiAgentDisabled(isExternalUser(ID));
  }

  function isExternalUser(userID: string): boolean {
    const localDomains = [
      "constellation-builders.com",
      "constellationhb.com",
      "tss-yonder.com",
    ];
    const userDomain = userID.split("@")[1]?.toLowerCase() || "";
    return !localDomains.includes(userDomain);
  }

  function userRemoved() {
    userChanged("", "", "", "");
    setLoggedInUser(null);
    api.setConnectionName("");
    setConnectionNameSelected(false);
    setSelectedConnection("");
    setConnectionNames([]);
  }

  function handleRefresh() {
    setShouldRefresh(new Date());
  }

  function selectTab(tabValue: string) {
    const isSwitchingToTemplate = selectedTab !== "template" && tabValue == "template";
    const isSwitchingFromTemplate = selectedTab == "template" && tabValue !== "template";

    if (isSwitchingToTemplate && window.innerWidth < TEMPLATE_TASK_PANE_WIDTH) {
      preTemplateTaskPaneWidthRef.current = window.innerWidth;
      setTaskPaneWidth(TEMPLATE_TASK_PANE_WIDTH);
    } else if (isSwitchingFromTemplate && preTemplateTaskPaneWidthRef.current !== null) {
      setTaskPaneWidth(preTemplateTaskPaneWidthRef.current);
      preTemplateTaskPaneWidthRef.current = null;
    }

    setSelectedTab(tabValue);
    setForceAnalyzers(tabValue == "analyzers");
    setAiAgentActive(tabValue == "ai");
    setTemplateTabActive(tabValue == "template");
  }

  function handleTemplateSaveSuccess() {
    if (templateSaveNavigationTimeoutRef.current !== null) {
      window.clearTimeout(templateSaveNavigationTimeoutRef.current);
    }

    templateSaveNavigationTimeoutRef.current = window.setTimeout(() => {
      setIsBmxReport(true);
      selectTab("data");
      templateSaveNavigationTimeoutRef.current = null;
    }, 350);
  }

  function handleApiBaseUrlDialogClose() {
    setShowApiBaseUrlDialog(false);
    userRemoved();
  }

  useEffect(() => {
    if (apiBaseUrl) {
      const storedApiBaseUrl = localStorage.getItem("apiBaseUrl");
      if (storedApiBaseUrl && storedApiBaseUrl !== apiBaseUrl && userID) {
        setShowApiBaseUrlDialog(true);
        userRemoved();
      }
      setTimeout(() => {
        localStorage.setItem("apiBaseUrl", apiBaseUrl);
      }, 1000);
    }
  }, [apiBaseUrl, userID]);

  useEffect(() => {
    if (userID && (isBmxReport || templateTabActive) && !connectionNameSelected && authToken) {
      const fetchConnectionNames = async () => {
        try {
          const response = await api.fetch(
            'database-connection/connection-names',
            authToken,
            identityProvider
          );
          
          if (!response.ok) {
            throw await responseError(response, ErrorMessages.LoadConnectionsFailed);
          }

          const connections = await response.json();
          const names = connections.map((conn: { name: string }) => conn.name);
          
          if (names.length === 0 || names.length === 1) {
            // Empty or single item - store and continue
            const connectionName = names.length === 1 ? names[0] : "";
            api.setConnectionName(connectionName);
            setSelectedConnection(connectionName);
            setConnectionNameSelected(true);
          } else {
            // Multiple items - show selector
            setConnectionNames(names);
            setSelectedConnection(names[0]);
            setShowConnectionSelector(true);
          }
        } catch (err) {
          console.error('Error fetching connection names:', err);
          setError(getErrorMessage(err, ErrorMessages.LoadConnectionsFailed));
        }
      };

      fetchConnectionNames();
    }
  }, [userID, isBmxReport, templateTabActive, connectionNameSelected, authToken, identityProvider]);
  
  useEffect(() => {
    if (!userID || !authToken) {
      return undefined;
    }

    // Guarded so a sign-out mid-flight can't land a stale user and re-open the Template tab.
    let cancelled = false;
    fetchLoggedInUser(authToken, identityProvider)
      .then((user) => {
        if (!cancelled) {
          setLoggedInUser(user);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoggedInUser(null);
          setError(getErrorMessage(err, ErrorMessages.LoadUserFailed));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userID, authToken, identityProvider]);

  useEffect(() => {
    selectTab(isBmxReport ? "data" : "analyzers");
  }, [isBmxReport]);

  useEffect(() => {
    return () => {
      if (templateSaveNavigationTimeoutRef.current !== null) {
        window.clearTimeout(templateSaveNavigationTimeoutRef.current);
      }
    };
  }, []);

  const handleConnectionSelect = () => {
    if (selectedConnection !== "") {
      api.setConnectionName(selectedConnection);
      setConnectionNameSelected(true);
      setShowConnectionSelector(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    (window as any).Office?.onReady(() => {
      (window as any).Excel?.run(async (context: any) => {
        try {
          let sheet = context.workbook.worksheets.getItemOrNullObject("(configuration)");
          sheet.load("name");
          await context.sync();
          if (!cancelled && !sheet.isNullObject) {
            setIsBmxReport(true);
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          return context.sync();
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const errorRaised = (message: string) => {
    setError(message);
  };

  return (
    <div className={styles.root}>
      <Header
        logo="assets/logo-large.png"
        title={props.title}
        isInitialized={isInitialized}
        oktaIdToken={oktaIdToken}
        userID={userID}
        userName={userName}
        identityProvider={identityProvider}
        setIsInitialized={setIsInitialized}
        setIsLoading={setIsLoading}
        userChanged={userChanged}
        userRemoved={userRemoved}
        errorRaised={errorRaised}
      />
      {userID ? (
        <section style={{ padding: "0 8px", marginBottom: "8px", borderBottom: "1px solid #ccc", display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "0px" }}>
            <TabList selectedValue={selectedTab} onTabSelect={(_, tab) => selectTab(String(tab.value))} style={{ marginRight: "-12px" }}>
              <Tab value="analyzers" style={{ paddingLeft: "6px", paddingRight: "6px" }}>Analyzers</Tab>
              <Tab value="data" disabled={!isBmxReport} style={{ paddingLeft: "6px", paddingRight: "6px" }}>Data</Tab>
              <Tab value="template" disabled={!loggedInUser?.isTemplateEditor} style={{ paddingLeft: "6px", paddingRight: "6px" }}>Template</Tab>
              {/* AI Assist tab hidden for now — restore by uncommenting
              { !aiAgentDisabled &&
                <Tab value="ai" style={{ paddingLeft: "6px", paddingRight: "6px" }}>AI Assist</Tab>
              }
              */}
            </TabList>
          </div>
          <div style={{ flexGrow: 1 }} />
          { (!isBmxReport || forceAnalyzers) && !aiAgentActive && !templateTabActive &&
            <FluentButton
              appearance="subtle"
              icon={<RefreshIcon />}
              onClick={() => handleRefresh()}
            />}
        </section>) : null}
      <div className={styles.content}>
        <ErrorBoundary>
        { isLoading ? (
          <p>Please wait…</p>
        ) : !userID ? (
          <div>
            <h3>Welcome to BuilderMetrix® for Excel!</h3>
            <ul>
              <li>This tool can be used to get data from BuilderMetrix® system.</li>
              <li>To get started, sign in from the top right button.</li>
            </ul>
          </div>
        ) : !(isBmxReport && !templateTabActive) || connectionNameSelected ? (
          aiAgentActive ? (
            <AiAgent authToken={authToken} identityProvider={identityProvider} />
          ) : templateTabActive ? (
            <TemplateTab authToken={authToken} identityProvider={identityProvider} onSaveSuccess={handleTemplateSaveSuccess} />
          ) : !isBmxReport || forceAnalyzers ? (
            <ReportsTree authToken={authToken} identityProvider={identityProvider} shouldRefresh={shouldRefresh} />
          ) : (
            <ReportParams authToken={authToken} identityProvider={identityProvider} shouldRefresh={shouldRefresh} />
          )
        ) : (
          <p>Loading connection configuration...</p>
        ) }
        </ErrorBoundary>
      </div>
      <footer className={styles.versionFooter}>
        <p className={styles.versionText}>{props.version}</p>
      </footer>
      <Dialog open={(error?.length ?? 0) > 0}>
        <DialogSurface style={{ margin: "16px" }}>
          <DialogBody>
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <p style={{ color: "red" }}>
                An error has occurred:
                <br />
                {error}
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button onClick={() => setError("")} label="Close" />
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={showApiBaseUrlDialog}>
        <DialogSurface style={{ margin: "16px" }}>
          <DialogBody>
            <DialogTitle>Configuration update</DialogTitle>
            <DialogContent>
              <p>
                The server side configuration has changed. Please sign in again to continue.
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button onClick={handleApiBaseUrlDialogClose} label="OK" />
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={showConnectionSelector}>
        <DialogSurface style={{ margin: "10px" }}>
          <DialogBody>
            <DialogTitle>Select Database Connection</DialogTitle>
            <DialogContent>
              <p>Please select a database connection to continue:</p>
              <Dropdown
                value={selectedConnection}
                onOptionSelect={(_, data) => setSelectedConnection(data.optionValue || "")}
              >
                {connectionNames.map((name) => (
                  <Option key={name} value={name}>{name}</Option>
                ))}
              </Dropdown>
            </DialogContent>
            <DialogActions>
              <FluentButton
                style={{ marginLeft: "auto" }}
                onClick={handleConnectionSelect}
                disabled={selectedConnection === ""}
              >
                Continue
              </FluentButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default App;
